import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Blumenautomat kaufen“ (/blumenautomat).
 * Ziel-Keyword „blumenautomat“ (~1.500/Monat) – größtes ambient-Volumen im Cluster.
 * Muster/SEO wie die übrigen Cluster-Seiten. Fokus: Floristik/Gärtnerei-Direktvermarktung.
 * Kein Kühl-Versprechen (keine Kühlung verfügbar) → Sträuße, Topfpflanzen, Trockenblumen.
 */
@Component({
  selector: 'app-blumenautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './blumenautomat-page.component.html',
  styleUrls: ['./blumenautomat-page.component.scss', '../cluster-landing.shared.scss'],
})
export class BlumenautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'wege-der-landwirtschaftlichen-direktvermarktung';
  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;
  /** Öffentlicher Live-Link als Feature-Demo (Echtzeit-Verfügbarkeit). */
  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/gartencenter-pflieger/U-MB-00563'
  );

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@bma.faq1.q:Was kostet ein Blumenautomat?`,
      a: $localize`:@@bma.faq1.a:Die marktbox als Blumenautomat gibt es ab 6.800 € zum Kauf oder ab 300 € pro Monat zur Miete – je nach Modell (24 oder 39 Fächer) und Zubehör.`,
    },
    {
      q: $localize`:@@bma.faq2.q:Welche Blumen eignen sich für den Automaten?`,
      a: $localize`:@@bma.faq2.a:Gut geeignet sind Sträuße, Topfpflanzen und Trockenblumen mit zügigem Abverkauf. Eine Kühlung ist aktuell nicht verfügbar – für lange Standzeiten empfindlicher Schnittblumen ist der Automat daher nur bedingt gedacht.`,
    },
    {
      q: $localize`:@@bma.faq3.q:Blumenautomat kaufen oder mieten?`,
      a: $localize`:@@bma.faq3.a:Beides ist möglich. Kaufen lohnt sich bei dauerhaftem Betrieb, Mieten senkt die Einstiegshürde. Eine ausführliche Entscheidungshilfe findest du in unserem Ratgeber.`,
    },
    {
      q: $localize`:@@bma.faq4.q:Wo kann ich einen Blumenautomaten aufstellen?`,
      a: $localize`:@@bma.faq4.a:Die marktbox lässt sich flexibel aufstellen – vor der Gärtnerei, am Hof, an Friedhöfen oder gut sichtbaren Standorten mit Laufkundschaft. Wichtig sind Sichtbarkeit und einfache Erreichbarkeit.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@bma.metaTitle:Blumenautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@bma.metaDesc:Blumenautomat kaufen oder mieten: Sträuße & Topfpflanzen 24/7 verkaufen – bargeldlos, ohne Personal. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content:
        $localize`:@@bma.metaKeywords:Blumenautomat, Blumenautomat kaufen, Blumen verkaufen Automat, Blumenautomat mieten, Floristik Automat, Direktvermarktung, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('blumen-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Blumenautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('blumen-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/blumenautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
